// Consent.tsx — OAuth consent screen on react-bootstrap.
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import ListGroup from 'react-bootstrap/ListGroup'
import { BsCheck2, BsCheckCircleFill, BsX } from 'react-icons/bs'

const scopeLabels: Record<string, { label: string; description: string }> = {
    openid: { label: 'Your identity', description: 'Verify who you are' },
    profile: { label: 'Profile information', description: 'Your name and public profile' },
    email: { label: 'Email address', description: 'View your email address' },
}

const Consent = () => {
    const [searchParams] = useSearchParams()
    const [loading, setLoading] = useState(false)
    const [error] = useState('')
    const [clientName, setClientName] = useState('')
    const [scopes, setScopes] = useState<string[]>([])

    const clientId = searchParams.get('client_id') || ''
    const redirectUri = searchParams.get('redirect_uri') || ''
    const responseType = searchParams.get('response_type') || ''
    const scopeParam = searchParams.get('scope') || ''
    const state = searchParams.get('state') || ''

    useEffect(() => {
        const parsed = scopeParam.split(' ').filter(Boolean)
        setScopes(parsed)

        const fetchClient = async () => {
            try {
                const res = await fetch(`/api/oauth/clients/${clientId}`)
                if (res.ok) {
                    const data = await res.json()
                    setClientName(data.name || clientId)
                } else {
                    setClientName(clientId)
                }
            } catch {
                setClientName(clientId)
            }
        }
        if (clientId) fetchClient()
    }, [clientId, scopeParam])

    const handleAllow = () => {
        setLoading(true)
        const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: responseType,
            scope: scopeParam,
            state: state,
            consent: 'allow',
        })
        window.location.href = `/api/authorize?${params.toString()}`
    }

    const handleDeny = () => {
        const url = new URL(redirectUri)
        url.searchParams.set('error', 'access_denied')
        if (state) url.searchParams.set('state', state)
        window.location.href = url.toString()
    }

    return (
        <div className="auth-wrapper">
            <Card style={{ width: '450px', maxWidth: '100%' }}>
                <Card.Body>
                    <h2 className="text-center mb-3">Authorization Request</h2>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <p className="text-center text-muted">
                        <strong className="text-body fw-bold">{clientName || 'An application'}</strong> wants to access your account
                    </p>
                    <p className="text-center text-muted small mb-3">This will allow the application to:</p>

                    <ListGroup variant="flush" className="mb-3">
                        {scopes.map((scope) => {
                            const info = scopeLabels[scope] || { label: scope, description: `Access: ${scope}` }
                            // `card` is the borderless surface (C-CARD-4), but
                            // .card also sets flex-direction: column, which would
                            // stack the tick above the text. flex-row restores it.
                            return (
                                <ListGroup.Item key={scope} className="d-flex flex-row align-items-start gap-2 card mb-2">
                                    <BsCheckCircleFill className="text-success mt-1" />
                                    <div>
                                        <div>{info.label}</div>
                                        <div className="text-muted small">{info.description}</div>
                                    </div>
                                </ListGroup.Item>
                            )
                        })}
                    </ListGroup>

                    <div className="d-flex gap-2">
                        <Button variant="outline-danger" onClick={handleDeny} disabled={loading} className="flex-fill d-flex align-items-center justify-content-center gap-2">
                            <BsX /> Deny
                        </Button>
                        <Button variant="success" onClick={handleAllow} disabled={loading} className="flex-fill d-flex align-items-center justify-content-center gap-2">
                            <BsCheck2 /> {loading ? 'Allowing…' : 'Allow'}
                        </Button>
                    </div>

                    <div className="text-center mt-3 small text-muted">
                        You can revoke this access at any time from your account settings.
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}

export default Consent
